import { useCallback, useEffect, useState } from 'react'

import {
  fetchAllPayrolls,
  fetchAllPaySchedules,
  fetchContractors,
  fetchEmployees,
  fetchPayrollWcEnrollmentRequirements,
} from '../payrollActions'
import { getFrequencyText } from '../helpers'
import {
  selectNetPayroll,
  selectNumberOfTeamMembers,
  selectPayrolls,
  selectPaySchedule,
} from '../payroll.selectors'
import { formatCurrency } from '@/utils/currencyHelpers'
import {
  Button,
  Text,
  Link,
  Alert,
  Separator,
  Icon,
  Dialog,
  Card,
  CardColors,
} from '@/ui'
import { PayrollScheduleForm } from '../enroll/PayrollScheduleForm'
import { NextWcEnrollmentPanel } from './WorkersCompensationTab'
import { WcEnrollmentRequirement } from '../payrollWcEnrollmentRequirement.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { Gusto_PaySchedule } from '../generated_gusto_types'
import { clearPayrolls } from '../payroll.slice'
import { useReselector } from '@/utils/sharedHooks'
import { PaymentConfigSettings } from './PaymentConfigSettings'
import { faCalendarAlt, faSync } from '@fortawesome/pro-regular-svg-icons'
import { TypographyStyles } from '@/styles'
import { AutoEnrollModal } from '../run-payroll/AutoEnrollModal'

const WC_FEATURE_FLAG = process.env.VITE_WORKERS_COMP_ENABLED

const PaySchedulePanel = ({
  paySchedule,
  openModal,
}: {
  paySchedule?: Gusto_PaySchedule | null
  openModal: () => void
}) => {
  if (!paySchedule) {
    return null
  }

  const autoText = paySchedule.auto_payroll ? 'On' : 'Off'
  return (
    <Card color={CardColors.LEVEL_0}>
      <Text variant={TypographyStyles.H3} className="mb-4">
        Pay Schedule
      </Text>

      <div className="flex flex-wrap justify-between items-start mb-2">
        <div className="flex flex-row w-full md:w-2/3">
          <Icon icon={faCalendarAlt} className="mr-2 inline" />
          <Text variant={TypographyStyles.BODY_SM} className="mr-2">
            Pay Frequency:
          </Text>
          <Text variant={TypographyStyles.BODY_SM}>
            {getFrequencyText(paySchedule)}
          </Text>
        </div>
        <div className="w-full md:w-1/3">
          <Text>
            Need to change this?{' '}
            <Link href="/conversations">Contact Support</Link>
          </Text>
        </div>
      </div>

      <Separator />

      <div className="flex items-center justify-between mt-4 mb-2">
        <div className="flex flex-row w-full md:w-2/3">
          <Icon icon={faSync} className="mr-2 inline" />
          <Text variant={TypographyStyles.BODY_SM} className="mr-2">
            Automatic Payroll:
          </Text>
          <Text variant={TypographyStyles.BODY_SM}>{autoText}</Text>
        </div>
        <div className="w-full md:w-1/3">
          <Button onClick={openModal} variant="link" size="sm">
            Edit
          </Button>
        </div>
      </div>
      <Separator />

      <div className="mt-4">
        <PaymentConfigSettings />
      </div>
    </Card>
  )
}

export const PayrollDetailsTab = () => {
  const dispatch = useAppDispatch()
  const [fetched, setFetched] = useState(false)
  const { netPaidToEmployees, netPayrollTaxes } =
    useReselector(selectNetPayroll)
  const numberOfTeamMembers = useReselector(selectNumberOfTeamMembers)
  const payrolls = useReselector(selectPayrolls)
  const [autoEnrollModalOpen, setAutoEnrollModalOpen] = useState(false)
  const paySchedule = useReselector(selectPaySchedule)
  const [payrollScheduleModalOpen, setPayrollScheduleModalOpen] =
    useState(false)
  const [enrollmentRequirements, setEnrollmentRequirements] =
    useState<WcEnrollmentRequirement>()

  useEffect(() => {
    const fetch = async () => {
      const [requirementsRes] = await Promise.all([
        fetchPayrollWcEnrollmentRequirements()(dispatch),
        fetchEmployees()(dispatch),
        fetchContractors()(dispatch),
        fetchAllPayrolls({ include: ['totals'] })(dispatch),
        fetchAllPaySchedules()(dispatch),
      ])

      if (requirementsRes) {
        setEnrollmentRequirements(requirementsRes)
      }
      setFetched(true)
    }

    fetch()
  }, [dispatch])

  const onPayrollScheduleSet = useCallback(async () => {
    setPayrollScheduleModalOpen(false)

    setFetched(false)
    await fetchAllPaySchedules()(dispatch)
    setFetched(true)
  }, [dispatch])

  const onClose = useCallback(() => {
    setAutoEnrollModalOpen(false)
    // Changing the pay schedule will invalidate upcoming payrolls - the previous ones need to be wiped and refreshed
    dispatch(clearPayrolls())
    dispatch(fetchAllPayrolls({ include: ['totals'] }))
  }, [dispatch])

  const notEnrolledInAuto = Boolean(paySchedule && !paySchedule.auto_payroll)

  return (
    <div className="flex flex-col gap-4">
      {WC_FEATURE_FLAG && fetched && enrollmentRequirements && (
        <NextWcEnrollmentPanel
          enrollmentRequirements={enrollmentRequirements}
          suppressIfEnrolled
        />
      )}

      {paySchedule && (
        <AutoEnrollModal
          modalOpen={autoEnrollModalOpen}
          onClose={onClose}
          paySchedule={paySchedule}
        />
      )}

      {fetched && !paySchedule && (
        <Alert variant="error">
          It looks like you don&apos;t have a payroll schedule{' '}
          <Button
            variant="link"
            onClick={() => setPayrollScheduleModalOpen(true)}
          >
            Click to set
          </Button>
          <Dialog
            open={payrollScheduleModalOpen}
            onOpenChange={setPayrollScheduleModalOpen}
          >
            <Dialog.Content>
              <Dialog.Title>Set Payroll Schedule</Dialog.Title>
              <PayrollScheduleForm
                onSuccess={onPayrollScheduleSet}
                onCancel={() => setPayrollScheduleModalOpen(false)}
                saveText="Save"
              />
            </Dialog.Content>
          </Dialog>
        </Alert>
      )}

      {notEnrolledInAuto && (
        <Alert variant="warning" className="mt-4">
          <Text variant={TypographyStyles.BODY_MD}>
            It looks like you haven&apos;t set up Automatic Payroll! We
            recommend setting it up for your ease of use.
          </Text>
          <div className="max-w-xs mt-4">
            <Button
              variant="primary"
              onClick={() => setAutoEnrollModalOpen(true)}
            >
              Set up now
            </Button>
          </div>
        </Alert>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1">
          <PaySchedulePanel
            paySchedule={paySchedule}
            openModal={() => setAutoEnrollModalOpen(true)}
          />
        </div>

        {fetched && (
          <Card color={CardColors.LEVEL_0}>
            <div className="mb-4">
              <Text variant={TypographyStyles.H3}>Your Payroll Summary</Text>
              <Text variant={TypographyStyles.BODY_MD} className="mt-2">
                Take a look at some quick stats about your Payroll!
              </Text>
            </div>
            <Separator className="my-2" />

            <div className="flex justify-between py-2">
              <Text variant={TypographyStyles.BODY_MD}>
                <b>Number of Payrolls Run</b>
              </Text>
              <Text>{Object.values(payrolls).length} total</Text>
            </div>
            <Separator className="my-2" />

            <div className="flex justify-between py-2">
              <Text variant={TypographyStyles.BODY_MD}>
                <b>Total number of Team Members</b>
              </Text>
              <Text variant={TypographyStyles.BODY_MD}>
                {numberOfTeamMembers}
              </Text>
            </div>
            <Separator className="my-2" />

            <div className="flex justify-between py-2">
              <Text variant={TypographyStyles.BODY_MD}>
                <b>Total net paid to Employees</b>
              </Text>
              <Text variant={TypographyStyles.BODY_MD}>
                {formatCurrency(netPaidToEmployees)}
              </Text>
            </div>
            <Separator className="my-2" />

            <div className="flex justify-between py-2">
              <Text variant={TypographyStyles.BODY_MD}>
                <b>Total paid in Payroll Taxes</b>
              </Text>
              <Text variant={TypographyStyles.BODY_MD}>
                {formatCurrency(netPayrollTaxes)}
              </Text>
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}
